import React, { useEffect, useState, useCallback, ReactNode } from 'react';
import { cachedFetch, IDENTITY_STALE_MS } from '@/lib/queryClient';
import { cloudClient } from '@/integrations/supabase/cloudClient';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureFlags } from '@/contexts/FeatureFlagContext';
import {
  EntitlementContext,
  EntitlementContextType,
  Profile,
  SubscriptionPlan,
  UserSubscription,
  PanelAccessRow,
} from './entitlement-context';

export { useEntitlements } from './entitlement-context';
export type { ApprovalStatus, SubscriptionStatus } from './entitlement-context';

const ADMIN_CATEGORIES = ['installation', 'rental', 'touring', 'studio'];

const ENT_CACHE_PREFIX = 'ledlogic-entitlements-v1:';
type EntCache = {
  profile: Profile | null;
  subscription: UserSubscription | null;
  memberships: SubscriptionPlan[];
  effectiveFeatures: string[];
  effectivePanelCategories: string[];
  panelAccess: PanelAccessRow[];
};
const readEntCache = (uid: string): EntCache | null => {
  try {
    const raw = sessionStorage.getItem(ENT_CACHE_PREFIX + uid);
    return raw ? (JSON.parse(raw) as EntCache) : null;
  } catch { return null; }
};
const writeEntCache = (uid: string, data: EntCache) => {
  try { sessionStorage.setItem(ENT_CACHE_PREFIX + uid, JSON.stringify(data)); } catch { /* ignore */ }
};

export const EntitlementProvider = ({ children }: { children: ReactNode }) => {
  const { user, isAdmin, authReady } = useAuth();
  const userId = user?.id ?? null;
  const { isFeatureEnabled } = useFeatureFlags();
  const initialCache = userId ? readEntCache(userId) : null;
  const [loading, setLoading] = useState(!initialCache);
  const [profile, setProfile] = useState<Profile | null>(initialCache?.profile ?? null);
  const [subscription, setSubscription] = useState<UserSubscription | null>(initialCache?.subscription ?? null);
  const [memberships, setMemberships] = useState<SubscriptionPlan[]>(initialCache?.memberships ?? []);
  const [effectiveFeatures, setEffectiveFeatures] = useState<string[]>(initialCache?.effectiveFeatures ?? []);
  const [effectivePanelCategories, setEffectivePanelCategories] = useState<string[]>(initialCache?.effectivePanelCategories ?? []);
  const [panelAccess, setPanelAccess] = useState<PanelAccessRow[]>(initialCache?.panelAccess ?? []);

  const load = useCallback(async () => {
    if (!authReady) { setLoading(true); return; }
    if (!user) {
      setProfile(null);
      setSubscription(null);
      setMemberships([]);
      setEffectiveFeatures([]);
      setEffectivePanelCategories([]);
      setPanelAccess([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const fetchProfile = async () => {
        let res = await cloudClient.from('profiles').select('*').eq('id', user.id).maybeSingle();
        if (!res.data && !res.error) {
          await new Promise(r => setTimeout(r, 400));
          res = await cloudClient.from('profiles').select('*').eq('id', user.id).maybeSingle();
        }
        return res;
      };

      // Single request set per user per minute — StrictMode double-mounts and
      // re-renders share the in-flight promise instead of re-querying.
      const [profileRes, subRes, entRes, paRes, memRes] = await cachedFetch(
        ['entitlements', user.id],
        () =>
          Promise.all([
            fetchProfile(),
            cloudClient.from('user_subscriptions').select('*').eq('user_id', user.id).maybeSingle(),
            cloudClient.from('user_entitlements').select('feature_key, enabled').eq('user_id', user.id),
            cloudClient.from('user_panel_access').select('*').eq('user_id', user.id),
            cloudClient
              .from('user_plan_memberships' as any)
              .select('plan_id, status')
              .eq('user_id', user.id)
              .in('status', ['trial', 'active']),
          ]),
        IDENTITY_STALE_MS,
      );

      const prof = (profileRes.data as Profile) || null;
      const sub = (subRes.data as UserSubscription) || null;
      setProfile(prof);
      setSubscription(sub);
      setPanelAccess((paRes.data as PanelAccessRow[]) || []);

      const memberPlanIds = ((memRes.data as unknown as Array<{ plan_id: string }>) || []).map(m => m.plan_id);
      let plans: SubscriptionPlan[] = [];
      if (memberPlanIds.length > 0) {
        const plansRes = await cloudClient
          .from('subscription_plans')
          .select('*')
          .in('id', memberPlanIds);
        plans = (plansRes.data as SubscriptionPlan[]) || [];
      }
      setMemberships(plans);

      const overrides = (entRes.data || []) as Array<{ feature_key: string; enabled: boolean }>;
      const overrideOn = overrides.filter(o => o.enabled).map(o => o.feature_key);
      const overrideOff = new Set(overrides.filter(o => !o.enabled).map(o => o.feature_key));

      let features: string[] = [];
      let cats: string[] = [];
      if (isAdmin) {
        features = ['*'];
        cats = [...ADMIN_CATEGORIES];
      } else if (prof?.approval_status !== 'approved') {
        features = [];
        cats = [];
      } else {
        const planFeatures = plans.flatMap(p => p.included_features || []);
        features = [...planFeatures, ...overrideOn].filter(f => !overrideOff.has(f));
        cats = Array.from(new Set(plans.flatMap(p => p.granted_panel_categories || [])));
      }
      const effF = Array.from(new Set(features));
      setEffectiveFeatures(effF);
      setEffectivePanelCategories(cats);
      writeEntCache(user.id, {
        profile: prof,
        subscription: sub,
        memberships: plans,
        effectiveFeatures: effF,
        effectivePanelCategories: cats,
        panelAccess: (paRes.data as PanelAccessRow[]) || [],
      });
    } catch (err) {
      console.error('Failed to load entitlements:', err);
    } finally {
      setLoading(false);
    }
  }, [userId, isAdmin, authReady]);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      setSubscription(null);
      setMemberships([]);
      setEffectiveFeatures([]);
      setEffectivePanelCategories([]);
      setPanelAccess([]);
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const hasFeature = useCallback(
    (key: string) => {
      if (!isFeatureEnabled(key)) return isAdmin;
      if (isAdmin) return true;
      if (effectiveFeatures.includes('*')) return true;
      return effectiveFeatures.includes(key);
    },
    [effectiveFeatures, isAdmin, isFeatureEnabled]
  );

  const hasPanelAccess = useCallback(
    (panelId: string) => {
      if (isAdmin) return true;
      if (profile?.approval_status !== 'approved') return false;
      return panelAccess.some(pa => pa.access_type === 'panel' && pa.panel_id === panelId);
    },
    [panelAccess, profile, isAdmin]
  );

  const hasPanelCategoryAccess = useCallback(
    (category: string | null | undefined) => {
      if (isAdmin) return true;
      if (!category) return false;
      return effectivePanelCategories.includes(category);
    },
    [effectivePanelCategories, isAdmin]
  );

  const currentPlan = memberships[0] ?? null;
  const isEnterprise = memberships.some(p => p.is_enterprise);
  const isBeta = memberships.some(p => p.key === 'beta_internal');

  const value: EntitlementContextType = {
    loading,
    profile,
    approvalStatus: profile?.approval_status ?? null,
    currentPlan,
    memberships,
    subscription,
    effectiveFeatures,
    effectivePanelCategories,
    panelAccess,
    hasFeature,
    hasPanelAccess,
    hasPanelCategoryAccess,
    isEnterprise,
    isBeta,
    refresh: load,
  };

  return <EntitlementContext.Provider value={value}>{children}</EntitlementContext.Provider>;
};
